<script lang="ts">
  import BaseStep from "./BaseStep.svelte";
  import SharedHeading from "../SharedHeading.svelte";
  import { getCopy } from "../../lib/stores/copy";
  import { settingsStore } from "../../lib/stores/settings";
  import {
    answerSurveyQuestion,
    getSurveyCopy,
    initSurvey,
    nextHandler,
    backHandler,
    type SurveyQuestion,
    isFollowUpSurvey
  } from "../../lib/helpers/steps/survey";
  import { surveyStore, type SurveyStore } from "../../lib/stores/survey";
  import RadioInput from "../Inputs/Radio.svelte";
  import isEmpty from "lodash/isEmpty";
  import Button from "../Button.svelte";
  import Checkbox from "../Inputs/Checkbox.svelte";
  import TextArea from "../Inputs/TextArea.svelte";
  import CustomHtml from "../CustomHTML.svelte";
  import { onMount } from "svelte";
  import { clearAllowListExceptTheCurrentRoute, navigateTo } from "../../lib/routing";
  import { NavigationType } from "../../lib/stores/session";
  import Youtube from "../Youtube.svelte";

  export let currentRoute: { name: string };

  let surveyCopy: Record<string, string> = {};
  let options: string[];
  let isNextDisabled = false;
  let isLoading = false;

  onMount(() => {
    // skip survey if the user has already completed once and survey is not a follow-up survey
    if ($surveyStore.isComplete && !isFollowUpSurvey()) {
      navigateTo(NavigationType.Next);
    } else {
      // always clear the allow list except for the survey page
      clearAllowListExceptTheCurrentRoute(currentRoute.name);
    }
  });

  initSurvey($settingsStore, currentRoute.name);

  const goToNext = async () => {
    isLoading = true;
    await nextHandler();
    isLoading = false;
  };

  const handleTextQuestionAnswer = (value: string) => {
    answerSurveyQuestion(questionIndex, 0, value, surveyQuestion?.type);
  };

  /**
   * It doesn't matter if the survey is complete or not,
   * if we are on the first question, we don't show the back button.
   * If we are not on the first question and the survey is not complete,
   * we show the back button.
   */
  const shouldShowBackButton = (surveyStore: SurveyStore | undefined) => {
    return (surveyStore?.currentIndex || 0) !== 0 && !surveyStore?.isComplete;
  };

  $: baseCopy = $getCopy("survey") as Record<string, string>;

  $: questionIndex = $surveyStore.currentIndex as number;
  $: surveyQuestion = $surveyStore?.questions?.[questionIndex] as SurveyQuestion;
  $: currentAnswers = $surveyStore.answers?.[questionIndex] || [];
  $: if (surveyQuestion) {
    surveyCopy = getSurveyCopy(baseCopy, surveyQuestion);
    options = surveyQuestion.options;
    if (surveyQuestion?.is_required) {
      isNextDisabled = isEmpty(
        currentAnswers.filter((value: string) => {
          return (value || "")?.trim();
        }) || []
      );
    }
  }
  $: isSurveyComplete = $surveyStore?.isComplete;
  $: showBackButton = shouldShowBackButton($surveyStore);
</script>

<BaseStep
  {currentRoute}
  customBackHandler={($surveyStore?.currentIndex || 0) > 0 ? backHandler : null}
  {showBackButton}>
  <svelte:fragment slot="content">
    {#if isSurveyComplete && isFollowUpSurvey()}
      <SharedHeading headerCopy={surveyCopy.followUpSurveyThankYouCopy} />
    {:else}
      <SharedHeading
        headerCopy={surveyCopy.headerCopy}
        subHeaderCopy={isFollowUpSurvey() ? "" : surveyCopy.subHeaderCopy} />
      <div class="mt-8">
        {#if surveyQuestion?.type === "text"}
          <TextArea
            placeholder={surveyCopy?.freeTextPlaceholder || ""}
            value={currentAnswers[0] || ""}
            isDisabled={isLoading}
            onChange={handleTextQuestionAnswer} />
        {:else if options?.length > 0}
          {#each options as option, optionIndex}
            <div class="option flex items-center">
              {#if surveyQuestion?.type === "choice"}
                <RadioInput
                  class="input-control"
                  value={currentAnswers[optionIndex]}
                  label={option}
                  onChange={() => answerSurveyQuestion(questionIndex, optionIndex, option, surveyQuestion?.type)}
                  isDisabled={isLoading} />
              {:else if surveyQuestion?.type === "multiple-choice"}
                <Checkbox
                  class="input-control"
                  value={currentAnswers[optionIndex]}
                  label={option}
                  onChange={() => answerSurveyQuestion(questionIndex, optionIndex, option, surveyQuestion?.type)}
                  isDisabled={isLoading} />
              {/if}
            </div>
          {/each}
        {/if}
      </div>

      <Button
        class="w-full lg:w-fit mt-4"
        isBusy={isLoading}
        disabled={isNextDisabled || isLoading}
        primary
        on:click={goToNext}>
        {surveyCopy.nextButtonCopy}
      </Button>
    {/if}

    <CustomHtml customHtml={surveyCopy.customHtml} />

    <Youtube videoId={surveyCopy.videoId} />
  </svelte:fragment>
</BaseStep>

<style>
  .option {
    border-radius: 0.5rem;
    background: var(--color-grayscale-10);
    margin-bottom: 1rem;
  }

  .option :global(.input-control) {
    padding: 1rem 1.5rem;
  }
</style>
